import { GLOBAL_TENANT, GLOBAL_TENANT_COMPOSITE } from './chunk-2XFFA546.js';
import { brandClass } from './chunk-N7SZ66QO.js';

// src/tenancy/defaults.ts
function stableFirst(items) {
  return items.reduce((best, it) => {
    const byTime = (it.created_at ?? 0) - (best.created_at ?? 0);
    if (byTime < 0) return it;
    if (byTime > 0) return best;
    return (it.id ?? "").localeCompare(best.id ?? "") < 0 ? it : best;
  });
}
function selectDefault(items, preferredId) {
  if (items.length === 0) return void 0;
  if (preferredId) {
    const preferred = items.find((it) => it.id === preferredId);
    if (preferred) return preferred;
  }
  const roots = items.filter((it) => it.is_root);
  if (roots.length > 0) return stableFirst(roots);
  const sysDefaults = items.filter((it) => it.system_default);
  if (sysDefaults.length > 0) return stableFirst(sysDefaults);
  return stableFirst(items);
}
function selectDefaultOrg(orgs, preferredId) {
  return selectDefault(orgs, preferredId);
}
function selectDefaultTenant(tenants, preferredId) {
  return selectDefault(tenants, preferredId);
}
function findRootOrg(orgs) {
  return orgs.find((o) => o.is_root);
}
function findRootTenant(tenants) {
  return tenants.find((t) => t.is_root);
}

// src/tenancy/structure.ts
var str = (v) => typeof v === "string" ? v : "";
var strOrNull = (v) => typeof v === "string" && v ? v : null;
var list = (v) => Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : [];
var strings = (v) => Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
function structureFromWire(raw) {
  const w = raw && typeof raw === "object" ? raw : {};
  const customer = w.customer && typeof w.customer === "object" ? w.customer : {};
  return {
    customer: { id: str(customer.id), name: str(customer.name) || str(customer.id) },
    orgs: list(w.orgs).map((o) => ({
      id: str(o.id),
      name: str(o.name) || str(o.id),
      isRoot: o.is_root === true,
      tenants: list(o.tenants).map((t) => ({
        id: str(t.id),
        name: str(t.name) || str(t.id),
        isRoot: t.is_root === true,
        teamsVisible: t.teams_visible !== false,
        teams: list(t.teams).map((team) => ({
          id: str(team.id),
          name: str(team.name) || str(team.id),
          isDefault: team.is_default === true,
          members: list(team.members).map((m) => ({ userId: str(m.user_id), roles: strings(m.roles) }))
        }))
      }))
    })),
    people: list(w.people).map((p) => ({
      userId: str(p.user_id),
      name: strOrNull(p.name),
      email: strOrNull(p.email),
      pending: p.pending === true,
      orgs: list(p.orgs).map((o) => ({
        orgId: str(o.org_id),
        status: str(o.status),
        tenants: list(o.tenants).map((g) => ({ tenantId: str(g.tenant_id), roles: strings(g.roles) }))
      }))
    })),
    hiddenOrgs: typeof w.hidden_orgs === "number" ? w.hidden_orgs : 0,
    viewerId: strOrNull(w.viewer_id)
  };
}
function personLabel(person) {
  return person.name || person.email || person.userId;
}
function accessMatrix(structure) {
  const columns = structure.orgs.flatMap(
    (org) => org.tenants.map((t) => ({ orgId: org.id, orgName: org.name, tenantId: t.id, tenantName: t.name }))
  );
  const teamsByUser = /* @__PURE__ */ new Map();
  for (const org of structure.orgs) {
    for (const tenant of org.tenants) {
      for (const team of tenant.teams) {
        for (const member of team.members) {
          const byTenant = teamsByUser.get(member.userId) ?? {};
          (byTenant[tenant.id] ??= []).push(team.name);
          teamsByUser.set(member.userId, byTenant);
        }
      }
    }
  }
  const rows = structure.people.map((person) => {
    const roles = {};
    for (const org of person.orgs) {
      for (const grant of org.tenants) {
        if (grant.roles.length) roles[grant.tenantId] = grant.roles;
      }
    }
    return { person, roles, teams: teamsByUser.get(person.userId) ?? {} };
  });
  return { columns, rows };
}
function peopleIn(structure, tenantId) {
  const out = [];
  for (const person of structure.people) {
    for (const org of person.orgs) {
      const grant = org.tenants.find((g) => g.tenantId === tenantId);
      if (grant && grant.roles.length) out.push({ person, roles: grant.roles });
    }
  }
  return out;
}
var MERMAID_ID = /[^A-Za-z0-9_]/g;
function mid(kind, id) {
  return `${kind}_${id.replace(MERMAID_ID, "_")}`;
}
function mlabel(text) {
  return `"${text.replace(/"/g, "#quot;")}"`;
}
function toMermaid(structure, options = {}) {
  const lines = ["flowchart TB"];
  const cus = mid("cus", structure.customer.id || "customer");
  lines.push(`  ${cus}[${mlabel(`${structure.customer.name} \xB7 customer`)}]`);
  for (const org of structure.orgs) {
    const orgId = mid("org", org.id);
    lines.push(`  subgraph ${orgId}[${mlabel(`${org.name}${org.isRoot ? " \xB7 root org" : ""}`)}]`);
    lines.push("    direction TB");
    for (const tenant of org.tenants) {
      const count = peopleIn(structure, tenant.id).length;
      const tenantId = mid("ten", tenant.id);
      lines.push(
        `    ${tenantId}[${mlabel(`${tenant.name}${tenant.isRoot ? " \xB7 root" : ""} \xB7 ${count} ${count === 1 ? "person" : "people"}`)}]`
      );
      for (const team of tenant.teams) {
        lines.push(
          `    ${mid("team", team.id)}([${mlabel(`${team.name}${team.isDefault ? " \xB7 default" : ""} \xB7 ${team.members.length}`)}])`
        );
        lines.push(`    ${tenantId} --> ${mid("team", team.id)}`);
      }
    }
    lines.push("  end");
    lines.push(`  ${cus} --> ${orgId}`);
  }
  if (structure.hiddenOrgs > 0) {
    lines.push(`  hidden[${mlabel(`${structure.hiddenOrgs} more ${structure.hiddenOrgs === 1 ? "organization" : "organizations"} you are not a member of`)}]`);
    lines.push(`  ${cus} -.-> hidden`);
  }
  if (options.people) {
    for (const row of accessMatrix(structure).rows) {
      const personId = mid("person", row.person.userId);
      const { name, email } = row.person;
      lines.push(`  ${personId}(${mlabel(name && email ? `${name}<br/>${email}` : personLabel(row.person))})`);
      for (const [tenantId, roles] of Object.entries(row.roles)) {
        lines.push(`  ${personId} -. ${mlabel(roles.join(", "))} .-> ${mid("ten", tenantId)}`);
      }
    }
  }
  return lines.join("\n");
}

// src/tenancy/index.ts
var TenantContext = class _TenantContext {
  static {
    brandClass(this, "tenancy/TenantContext");
  }
  customerId;
  orgId;
  tenantId;
  userId;
  constructor(init = {}) {
    this.customerId = init.customerId ?? null;
    this.orgId = init.orgId || GLOBAL_TENANT;
    this.tenantId = init.tenantId || GLOBAL_TENANT;
    this.userId = init.userId ?? null;
  }
  /** Composite key `{org_id}-{tenant_id}` used in DDB SKs + global-tier checks. */
  get orgIdTenantId() {
    return `${this.orgId}-${this.tenantId}`;
  }
  get isGlobal() {
    return this.orgIdTenantId === GLOBAL_TENANT_COMPOSITE;
  }
  get isUserScoped() {
    return this.userId != null && this.userId.length > 0;
  }
  get hasCustomerScope() {
    return this.customerId != null && this.customerId.length > 0 || this.orgId !== GLOBAL_TENANT;
  }
  withUser(userId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId
    });
  }
  withOrg(orgId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  withTenant(tenantId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId,
      userId: this.userId
    });
  }
  withCustomer(customerId) {
    return new _TenantContext({
      customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  toJSON() {
    return {
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId,
      orgIdTenantId: this.orgIdTenantId
    };
  }
};
function isGlobalTenantSegment(value) {
  if (value == null) return true;
  const trimmed = value.trim();
  return trimmed === "" || trimmed === GLOBAL_TENANT;
}
function coerceTenantContext(carrier) {
  if (!carrier) return new TenantContext();
  if (carrier instanceof TenantContext) return carrier;
  return new TenantContext(carrier);
}
function requireExplicitTenant(ctx) {
  if (ctx.isGlobal) {
    throw new Error(
      "Refusing to write under the global tenancy (main-main). Pass an explicit org_id/tenant_id."
    );
  }
}

export { TenantContext, accessMatrix, coerceTenantContext, findRootOrg, findRootTenant, isGlobalTenantSegment, peopleIn, personLabel, requireExplicitTenant, selectDefault, selectDefaultOrg, selectDefaultTenant, structureFromWire, toMermaid };
//# sourceMappingURL=chunk-MSSL2OR2.js.map
//# sourceMappingURL=chunk-MSSL2OR2.js.map